import { useState, useEffect, useRef } from 'react';
import { Eye, ChevronRight, ChevronDown, MapPin, Search, X, MessageSquare } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { StatusBadge } from '@/components/shared/status-badge';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useAdminTenants, useActivateTenant, useDeactivateTenant, useActivateLocation } from '@/hooks/use-admin';
import { formatDateTime } from '@/lib/utils';

type FilterMode = 'all' | 'active' | 'inactive' | 'pending-locations';

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const FILTER_BUTTONS: { mode: FilterMode; label: string }[] = [
  { mode: 'all', label: 'All' },
  { mode: 'active', label: 'Active' },
  { mode: 'inactive', label: 'Inactive' },
  { mode: 'pending-locations', label: 'Pending Locations' },
];

export function AdminDashboardPage() {
  const [page, setPage] = useState(1);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [searchInput, setSearchInput] = useState('');
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());
  const [confirmDeactivate, setConfirmDeactivate] = useState<{ id: string; name: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchInput, 300);

  const queryParams: Record<string, string | number | boolean | undefined> = { page, perPage: 25 };
  if (filterMode === 'active') queryParams.isActive = 'true';
  if (filterMode === 'inactive') queryParams.isActive = 'false';
  if (filterMode === 'pending-locations') queryParams.hasPendingLocations = 'true';
  if (debouncedSearch) queryParams.search = debouncedSearch;

  const { data, isLoading } = useAdminTenants(queryParams);
  const activateTenant = useActivateTenant();
  const deactivateTenant = useDeactivateTenant();
  const activateLocation = useActivateLocation();

  const tenantList: any[] = data?.data ?? [];
  const pagination = data?.pagination;

  // In 'pending-locations' mode auto-expand tenants that have pending locs; still allow
  // manual expansion of others. Derived — no useEffect needed.
  const effectiveExpanded =
    filterMode === 'pending-locations'
      ? new Set([...tenantList.filter((t: any) => t.pendingLocations > 0).map((t: any) => t.id), ...expandedTenants])
      : expandedTenants;

  function toggleExpanded(tenantId: string) {
    setExpandedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }
      return next;
    });
  }

  function handleFilterChange(mode: FilterMode) {
    setFilterMode(mode);
    setExpandedTenants(new Set());
    setPage(1);
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Businesses" description="Manage all registered businesses" />

      {/* Search + Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-56 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search by name, city…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-9 w-full rounded-lg border border-border bg-white pl-9 pr-8 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
          {searchInput && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex rounded-lg border border-border">
          {FILTER_BUTTONS.map(({ mode, label }, idx) => (
            <button
              key={mode}
              onClick={() => handleFilterChange(mode)}
              className={[
                'px-3 py-1.5 text-sm',
                filterMode === mode ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted',
                idx === 0 ? 'rounded-l-lg' : '',
                idx === FILTER_BUTTONS.length - 1 ? 'rounded-r-lg' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading…</div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-gray-50">
                <tr className="border-b border-border">
                  <th className="w-8 px-3 py-3.5" />
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Business Name
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">Owner</th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Status
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Modules
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Created
                  </th>
                  <th className="px-4 py-3.5 text-left text-[13px] font-semibold tracking-wide text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenantList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      No businesses found.
                    </td>
                  </tr>
                ) : (
                  tenantList.map((tenant) => {
                    const isExpanded = effectiveExpanded.has(tenant.id);
                    const hasLocations = (tenant.locations ?? []).length > 0;

                    return (
                      <>
                        {/* Tenant row */}
                        <tr
                          key={tenant.id}
                          className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                        >
                          {/* Expand toggle */}
                          <td className="px-3 py-3 text-center">
                            {hasLocations ? (
                              <button
                                onClick={() => toggleExpanded(tenant.id)}
                                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            ) : null}
                          </td>

                          {/* Name */}
                          <td className="px-4 py-3 text-sm font-medium text-foreground">{tenant.name}</td>

                          {/* Owner */}
                          <td className="px-4 py-3 text-sm text-muted-foreground">{tenant.owner?.email ?? 'N/A'}</td>

                          {/* Status + pending badge */}
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <StatusBadge status={tenant.isActive ? 'active' : 'inactive'} />
                              {tenant.type === 'companion' && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-700">
                                  <MessageSquare className="h-2.5 w-2.5" />
                                  Messaging partner
                                </span>
                              )}
                              {tenant.pendingLocations > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                                  <MapPin className="h-2.5 w-2.5" />
                                  {tenant.pendingLocations} location{tenant.pendingLocations > 1 ? 's' : ''} pending
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Modules — empty on tenant row; shown per location */}
                          <td className="px-4 py-3" />

                          {/* Created */}
                          <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(tenant.createdAt)}
                          </td>

                          {/* Actions */}
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <a
                                href={`#/admin/tenants/${tenant.id}`}
                                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                              >
                                <Eye className="h-3 w-3" />
                                View
                              </a>
                              {tenant.isActive ? (
                                <button
                                  onClick={() => setConfirmDeactivate({ id: tenant.id, name: tenant.name })}
                                  disabled={deactivateTenant.isPending}
                                  className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                                >
                                  Deactivate
                                </button>
                              ) : (
                                <button
                                  onClick={() => activateTenant.mutate(tenant.id)}
                                  disabled={activateTenant.isPending}
                                  className="rounded-md border border-green-200 px-2 py-1 text-xs text-green-600 hover:bg-green-50"
                                >
                                  Activate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded locations sub-rows */}
                        {isExpanded &&
                          (tenant.locations ?? []).map((loc: any) => (
                            <tr
                              key={`${tenant.id}-${loc.id}`}
                              className="border-b border-border last:border-0 bg-muted/20"
                            >
                              {/* indent */}
                              <td className="px-3 py-2" />
                              {/* Location name + address */}
                              <td className="px-4 py-2" colSpan={1}>
                                <div className="flex items-center gap-2 pl-4">
                                  <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                  <div>
                                    <p className="text-sm font-medium text-foreground">{loc.name}</p>
                                    {loc.address && <p className="text-xs text-muted-foreground">{loc.address}</p>}
                                  </div>
                                </div>
                              </td>
                              {/* Owner col — empty, inherits from parent */}
                              <td className="px-4 py-2" />
                              {/* Location status */}
                              <td className="px-4 py-2">
                                <StatusBadge status={loc.isActive ? 'active' : 'pending'} />
                              </td>
                              {/* Location modules */}
                              <td className="px-4 py-2">
                                {(loc.modules ?? []).length === 0 ? (
                                  <span className="text-xs text-muted-foreground">—</span>
                                ) : (
                                  <div className="flex flex-wrap gap-1">
                                    {(loc.modules ?? []).map((m: any) => (
                                      <span
                                        key={m.moduleName}
                                        className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium capitalize text-primary"
                                      >
                                        {m.moduleName}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </td>
                              {/* Created */}
                              <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateTime(loc.createdAt)}
                              </td>
                              {/* Activate / Deactivate location */}
                              <td className="px-4 py-2">
                                <button
                                  onClick={() =>
                                    activateLocation.mutate({
                                      tenantId: tenant.id,
                                      locationId: loc.id,
                                      isActive: !loc.isActive,
                                    })
                                  }
                                  disabled={activateLocation.isPending}
                                  className={
                                    loc.isActive
                                      ? 'rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50'
                                      : 'rounded-md border border-green-200 px-2 py-1 text-xs text-green-600 hover:bg-green-50'
                                  }
                                >
                                  {loc.isActive ? 'Deactivate' : 'Activate'}
                                </button>
                              </td>
                            </tr>
                          ))}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination && (
            <DataTablePagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              perPage={pagination.perPage}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Deactivate Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Deactivate Business"
        message={`Are you sure you want to deactivate "${confirmDeactivate?.name}"? This business and its users will lose access until reactivated.`}
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={deactivateTenant.isPending}
        onConfirm={() => {
          if (confirmDeactivate) {
            deactivateTenant.mutate(confirmDeactivate.id, {
              onSuccess: () => setConfirmDeactivate(null),
            });
          }
        }}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </div>
  );
}
